/**
 * One payment, end to end.
 *
 * "Why did ₹1,18,000 leave the bank on Tuesday?" should take one click, not
 * four screens. Read top to bottom this is the whole chain: who asked, what it
 * came from, who approved it and on what content, what was paid, which bank
 * line it matched, and the entry it became.
 *
 * Nothing here is editable. An auditor who can follow this page does not need
 * anybody to explain a payment.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api, formatInr } from '../api'
import { Card, Empty, ErrorNote, Loading, Note, Pill } from '../components/ui'

interface Decision {
  by: string | null
  on_behalf_of: string | null
  outcome: string
  decided_at: string
  reason: string | null
  still_stands: boolean
}

interface Step {
  step_no: number
  label: string
  required: number
  assigned_to: string | null
  note: string | null
  decisions: Decision[]
}

interface JournalLineRow {
  account_code: string
  account_name: string
  debit_paise: number
  credit_paise: number
  memo: string | null
}

interface Settlement {
  settlement_id: string
  amount_paise: number
  treatment: string
  charge_paise: number
  excess_paise: number
  short_closed_paise: number
  reason: string | null
  confirmed_by: string | null
  confirmed_at: string
  reversed_at: string | null
  reversal_reason: string | null
  bank_line: {
    id: string
    date: string
    amount_paise: number
    narration: string | null
    reference: string | null
    account: string | null
  } | null
  journal: {
    id: string
    journal_number: string
    journal_date: string
    narration: string | null
    lines: JournalLineRow[]
  } | null
}

interface TimelineRow {
  seq: number
  at: string
  action: string
  by: string | null
  role: string | null
  on_behalf_of: string | null
  object_type: string
  reason: string | null
  ip: string | null
  request_id: string | null
}

interface TraceData {
  request: {
    id: string
    reference: string
    kind: string
    title: string
    description: string | null
    amount_paise: number
    paid_paise: number
    outstanding_paise: number
    status: string
    required_by: string | null
    raised_by: string | null
    department: string | null
    location: string | null
    cost_centre: string | null
    vendor: string | null
    cancel_reason: string | null
  }
  origin: { kind: string; reference: string; detail: string; id: string } | null
  approvals: {
    source: string
    of: string
    state: string
    approved: boolean
    steps: Step[]
  }
  payments: {
    paid_on: string
    amount_paise: number
    utr: string | null
    recorded_by: string | null
    note: string | null
  }[]
  settlements: Settlement[]
  /** Null unless the reader may see the audit log. */
  timeline: TimelineRow[] | null
}

const ORIGIN_LABELS: Record<string, string> = {
  vendor_bill: 'Supplier bill',
  commitment: 'Commitment',
  loan: 'Loan',
  purchase_request: 'Purchase request',
}

export default function Trace() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ['trace', id],
    queryFn: () => api.get<TraceData>(`/spend/payment-requests/${id}/trace`),
    enabled: Boolean(id),
  })

  if (isLoading) return <Loading what="Following the money" />
  if (error) return <ErrorNote error={error} />
  if (!data) return null
  const { request, origin, approvals, payments, settlements, timeline } = data

  return (
    <>
      <p className="muted">
        <Link to="/spending/payment-requests">← Back to payment requests</Link>
      </p>

      <Card title={request.reference} hint={request.title} tight>
        <table>
          <tbody>
            <tr>
              <th>Amount</th>
              <td className="num">{formatInr(request.amount_paise)}</td>
              <th>Status</th>
              <td>
                <Pill value={request.status} />
              </td>
            </tr>
            <tr>
              <th>Paid</th>
              <td className="num">{formatInr(request.paid_paise)}</td>
              <th>Still owed</th>
              <td className="num">{formatInr(request.outstanding_paise)}</td>
            </tr>
            <tr>
              <th>Raised by</th>
              <td>{request.raised_by ?? '—'}</td>
              <th>Paid to</th>
              <td>{request.vendor ?? '—'}</td>
            </tr>
            <tr>
              <th>Department</th>
              <td className="muted">{request.department ?? '—'}</td>
              <th>Clinic</th>
              <td className="muted">{request.location ?? '—'}</td>
            </tr>
          </tbody>
        </table>
        {request.cancel_reason && <Note kind="warn">Cancelled: {request.cancel_reason}</Note>}
      </Card>

      <Card title="What it came from" tight>
        {origin ? (
          <div>
            <strong>{ORIGIN_LABELS[origin.kind] ?? origin.kind}</strong>{' '}
            <span className="mono">{origin.reference}</span>
            <div className="muted">{origin.detail}</div>
          </div>
        ) : (
          <Empty>
            A one-off request: nobody raised it from a bill, a commitment or a loan, so it
            carries its own approval and nothing else.
          </Empty>
        )}
      </Card>

      <Card
        title="Who approved it"
        hint={approvals.source === 'inherited' ? `carries the approval of ${approvals.of}` : 'its own route'}
        tight
      >
        <Note kind={approvals.approved ? 'ok' : 'warn'}>{approvals.state}</Note>
        {approvals.steps.length === 0 ? (
          <Empty>No approval steps are recorded against this document.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Step</th>
                <th>Who</th>
                <th>Decision</th>
                <th>When</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {approvals.steps.map((step) => (
                <tr key={step.step_no}>
                  <td>
                    {step.step_no}. {step.label}
                    {step.required > 1 && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {step.required} approvals needed
                      </div>
                    )}
                  </td>
                  <td className="muted">{step.assigned_to ?? 'anyone who may approve'}</td>
                  <td>
                    {step.decisions.length === 0 ? (
                      <span className="faint">not decided</span>
                    ) : (
                      step.decisions.map((d, index) => (
                        <div key={index}>
                          <Pill value={d.outcome} /> {d.by ?? 'unknown'}
                          {d.on_behalf_of && (
                            <span className="faint"> on behalf of {d.on_behalf_of}</span>
                          )}
                          {!d.still_stands && (
                            <div className="faint" style={{ fontSize: 12 }}>
                              lapsed — the document changed after this decision
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </td>
                  <td className="muted date">
                    {step.decisions.map((d, index) => (
                      <div key={index}>{d.decided_at?.slice(0, 16).replace('T', ' ')}</div>
                    ))}
                  </td>
                  <td className="muted">
                    {step.decisions.map((d, index) => (
                      <div key={index}>{d.reason ?? ''}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="What was paid" hint="recorded after the money left, in net banking" tight>
        {payments.length === 0 ? (
          <Empty>No payment has been recorded yet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Paid on</th>
                <th>UTR</th>
                <th>Recorded by</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, index) => (
                <tr key={index}>
                  <td className="muted date">{p.paid_on}</td>
                  <td className="mono">{p.utr ?? '—'}</td>
                  <td className="muted">{p.recorded_by ?? '—'}</td>
                  <td className="num">{formatInr(p.amount_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {timeline && timeline.length > 0 && (
        <Card
          title="Everything that happened to it"
          hint="the audit rows, traced by id rather than by name"
          tight
        >
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Who</th>
                <th>Why</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {timeline.map((row) => (
                <tr key={row.seq}>
                  <td className="muted date">{row.at?.slice(0, 16).replace('T', ' ')}</td>
                  <td>
                    <Pill value={row.action} />
                    <div className="faint" style={{ fontSize: 12 }}>
                      {row.object_type.replace(/_/g, ' ')}
                    </div>
                  </td>
                  <td>
                    {row.by ?? 'the system'}
                    {row.role && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {row.role.replace(/_/g, ' ')}
                      </div>
                    )}
                    {row.on_behalf_of && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        on behalf of {row.on_behalf_of}
                      </div>
                    )}
                  </td>
                  <td className="muted">{row.reason ?? ''}</td>
                  <td className="mono faint" style={{ fontSize: 12 }}>
                    {row.ip ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Note kind="info">
            Append-only and hash-chained: no one can edit these, including whoever
            wrote them. A row with no address was recorded before the request that
            caused it could be identified, or by work no request caused.
          </Note>
        </Card>
      )}

      <Card
        title="The bank line, and the entry it became"
        hint="only this step writes the ledger"
        tight
      >
        {settlements.length === 0 ? (
          <Empty>
            Nothing is matched to the statement yet, so none of this is in the books.
          </Empty>
        ) : (
          settlements.map((s) => (
            <div key={s.settlement_id} style={{ marginBottom: 20 }}>
              <p className="muted" style={{ marginTop: 0 }}>
                {formatInr(s.amount_paise)} matched by {s.confirmed_by ?? 'unknown'} on{' '}
                {s.confirmed_at?.slice(0, 10)} · <Pill value={s.treatment} />
                {s.reversed_at && (
                  <span> · reversed {s.reversed_at.slice(0, 10)}: {s.reversal_reason}</span>
                )}
              </p>
              {s.bank_line && (
                <table>
                  <thead>
                    <tr>
                      <th>Statement date</th>
                      <th>Account</th>
                      <th>Narration</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="muted date">{s.bank_line.date}</td>
                      <td className="muted">{s.bank_line.account ?? '—'}</td>
                      <td>{s.bank_line.narration ?? '—'}</td>
                      <td className="num">{formatInr(s.bank_line.amount_paise)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              {s.journal && (
                <table style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      <th>
                        Entry <span className="mono">{s.journal.journal_number}</span>
                      </th>
                      <th>Account</th>
                      <th className="num">Debit</th>
                      <th className="num">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.journal.lines.map((line, index) => (
                      <tr key={index}>
                        <td className="muted">{index === 0 ? s.journal?.journal_date : ''}</td>
                        <td>
                          <span className="mono">{line.account_code}</span> {line.account_name}
                          {line.memo && (
                            <div className="faint" style={{ fontSize: 12 }}>
                              {line.memo}
                            </div>
                          )}
                        </td>
                        <td className="num">
                          {line.debit_paise ? formatInr(line.debit_paise) : ''}
                        </td>
                        <td className="num">
                          {line.credit_paise ? formatInr(line.credit_paise) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))
        )}
      </Card>
    </>
  )
}
